// Status and category types
export type ExpenseStatus = 'draft' | 'pending' | 'approved' | 'rejected' | 'reimbursed';
export type ExpenseCategory = 'travel' | 'meals' | 'mileage' | 'supplies' | 'software' | 'lodging' | 'client_expense' | 'other';

// Main Expense interface
export interface Expense {
  id: number;
  user_id: number;
  organization_id: number;
  project_id?: number;
  task_id?: number;
  category: ExpenseCategory;
  merchant_name?: string;
  description?: string;
  amount: string; // decimal from backend
  currency: string; // 'CAD' default
  expense_date: string; // YYYY-MM-DD
  billable: boolean;
  reimbursable: boolean;
  status: ExpenseStatus;
  rejection_reason?: string;
  approved_by_id?: number;
  approved_at?: string;
  reimbursed_at?: string;
  created_at: string;
  updated_at: string;
  user: { id: number; full_name: string; email: string };
  project?: { id: number; name: string };
  approved_by?: { id: number; full_name: string; email: string };
  expense_attachments?: Array<{
    id: number;
    file_url: string;
    file_name: string;
    content_type: string;
    file_size: number;
    human_file_size: string;
    is_image: boolean;
    created_at: string;
  }>;
}

// Form data for create/update
export interface ExpenseFormData {
  amount: string;
  currency?: string;
  category: ExpenseCategory;
  merchant_name?: string;
  description?: string;
  expense_date: string;
  project_id?: number;
  billable?: boolean;
  reimbursable?: boolean;
  status?: ExpenseStatus;
}

// Summary for list view
export interface ExpenseSummary {
  total_count: number;
  total_amount: string;
  status_summary: {
    draft: { count: number; amount: string };
    pending: { count: number; amount: string };
    approved: { count: number; amount: string };
    rejected: { count: number; amount: string };
    reimbursed: { count: number; amount: string };
  };
}

// Report data
export interface ExpenseReport {
  period: { from: string; to: string };
  summary: {
    total_count: number;
    total_amount: string;
    by_status: Record<ExpenseStatus, { count: number; amount: string }>;
  };
  by_employee?: Array<{
    employee: { id: number; full_name: string };
    count: number;
    amount: string;
  }>;
}

export interface ExpenseLimit {
  id?: number;
  category: ExpenseCategory;
  period: 'monthly' | 'yearly';
  limit_amount: number;
  active: boolean;
}

export const EXPENSE_CATEGORIES: { value: ExpenseCategory; label: string }[] = [
  { value: 'travel', label: 'Travel' },
  { value: 'meals', label: 'Meals & Entertainment' },
  { value: 'mileage', label: 'Mileage' },
  { value: 'supplies', label: 'Office Supplies' },
  { value: 'software', label: 'Software & Subscriptions' },
  { value: 'lodging', label: 'Lodging' },
  { value: 'client_expense', label: 'Client Expense' },
  { value: 'other', label: 'Other' },
];

export const EXPENSE_STATUSES: { value: ExpenseStatus; label: string; color: string }[] = [
  { value: 'draft', label: 'Draft', color: '#9CA3AF' },
  { value: 'pending', label: 'Pending', color: '#F59E0B' },
  { value: 'approved', label: 'Approved', color: '#10B981' },
  { value: 'rejected', label: 'Rejected', color: '#EF4444' },
  { value: 'reimbursed', label: 'Reimbursed', color: '#06B6D4' },
];
